import { useEffect, useState } from 'preact/hooks';
import {
  BILLING_CYCLES,
  CONTRACT_CURRENCIES,
  CONTRACT_STATUSES,
  type ContractsBody,
  type ContractView,
} from '../../shared/contracts/contracts';
import type { SupplierListBody } from '../../shared/suppliers/review';
import { DateHint } from '../components/DateHint';
import { useI18n, type MessageKey } from '../i18n';
import { ApiError, apiGet, apiSend } from '../lib/api';
import { formatDate } from '../lib/dates';

// Contracts and renewals (screen 16, P3.5). Procurement keeps the register; the daily job alerts
// owners 90, 60 and 30 days before the end; "Create renewal PR" opens a new request prefilled
// from the contract and links it back once saved (D56).

type Load =
  | { kind: 'loading' }
  | { kind: 'error'; code: string; reference: string | null }
  | { kind: 'ok'; data: ContractsBody };

interface Form {
  poNo: string;
  name: string;
  supplierId: string;
  entity: string;
  department: string;
  owner: string;
  start: string;
  end: string;
  value: string;
  currency: string;
  billingCycle: string;
  renewalNoticeDays: string;
  autoRenew: boolean;
  notes: string;
}

const EMPTY: Form = {
  poNo: '',
  name: '',
  supplierId: '',
  entity: 'SMC',
  department: '',
  owner: '',
  start: '',
  end: '',
  value: '',
  currency: 'SAR',
  billingCycle: 'annualUpfront',
  renewalNoticeDays: '30',
  autoRenew: false,
  notes: '',
};

const whole = (minor: number) =>
  new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Math.round(minor / 100));

const compact = (minor: number) =>
  new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 2 }).format(
    minor / 100,
  );

const ENTITY_PILL: Record<string, string> = {
  SMC: 'pill--smc',
  VIB: 'pill--vibes',
  MED: 'pill--media',
};

export function ContractsPage() {
  const { t, lang } = useI18n();
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [reload, setReload] = useState(0);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState<Form>(EMPTY);
  const [suppliers, setSuppliers] = useState<{ id: string; name: string }[]>([]);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [edit, setEdit] = useState({ end: '', value: '', autoRenew: false, status: 'active' });

  useEffect(() => {
    let live = true;
    apiGet<ContractsBody>('/api/contracts')
      .then((data) => live && setLoad({ kind: 'ok', data }))
      .catch((err: unknown) => {
        if (!live) return;
        const e = err instanceof ApiError ? err : null;
        setLoad({ kind: 'error', code: e?.code ?? 'generic', reference: e?.correlationId ?? null });
      });
    return () => {
      live = false;
    };
  }, [reload]);

  useEffect(() => {
    if (!adding || suppliers.length) return;
    apiGet<SupplierListBody>('/api/suppliers')
      .then((b) =>
        setSuppliers(
          b.suppliers
            .filter((s) => s.status === 'approved')
            .map((s) => ({ id: s.supplierId, name: s.name }))
            .sort((a, b) => a.name.localeCompare(b.name)),
        ),
      )
      .catch(() => undefined);
  }, [adding, suppliers.length]);

  if (load.kind === 'loading') {
    return (
      <section class="card" aria-busy="true">
        <p>{t('req.loading')}</p>
      </section>
    );
  }
  if (load.kind === 'error') {
    return (
      <section class="card card--error" role="alert">
        <p>{load.code === 'forbidden' ? t('review.error.forbidden') : t('req.loadError')}</p>
        {load.reference && (
          <p class="caption" dir="ltr">
            {t('error.reference', { ref: load.reference })}
          </p>
        )}
      </section>
    );
  }
  const { data } = load;
  const set = (patch: Partial<Form>) => setForm({ ...form, ...patch });
  const upcoming = data.contracts.filter(
    (c) =>
      c.status !== 'ended' && c.status !== 'cancelled' && c.daysToEnd >= 0 && c.daysToEnd <= 90,
  );
  const bands = [
    { key: 'cn.band.30', items: upcoming.filter((c) => c.daysToEnd <= 30), notice: true },
    {
      key: 'cn.band.60',
      items: upcoming.filter((c) => c.daysToEnd > 30 && c.daysToEnd <= 60),
      notice: false,
    },
    { key: 'cn.band.90', items: upcoming.filter((c) => c.daysToEnd > 60), notice: false },
  ];

  async function fromPo() {
    setFields({});
    try {
      const p = await apiGet<Record<string, string | number>>(
        `/api/contracts/from-po?poNo=${encodeURIComponent(form.poNo.trim())}`,
      );
      const start = String(p.start ?? '');
      const end = start ? `${Number(start.slice(0, 4)) + 1}${start.slice(4)}` : '';
      setForm({
        ...form,
        name: String(p.name ?? ''),
        supplierId: String(p.supplierId ?? ''),
        entity: String(p.entity ?? 'SMC'),
        department: String(p.department ?? ''),
        owner: String(p.owner ?? ''),
        start,
        end,
        value: String(p.value ?? ''),
        currency: String(p.currency ?? 'SAR'),
        billingCycle: String(p.billingCycle ?? 'annualUpfront'),
      });
      if (p.supplierId && !suppliers.some((s) => s.id === p.supplierId)) {
        setSuppliers([
          ...suppliers,
          { id: String(p.supplierId), name: String(p.supplierName ?? '') },
        ]);
      }
    } catch {
      setFields({ poNo: t('cn.error.po') });
    }
  }

  async function save() {
    setBusy(true);
    setFields({});
    setNote(null);
    try {
      const out = await apiSend<ContractView>('POST', '/api/contracts', {
        name: form.name,
        supplierId: form.supplierId,
        entity: form.entity,
        department: form.department,
        owner: form.owner.trim(),
        start: form.start,
        end: form.end,
        value: Number(form.value) || 0,
        currency: form.currency,
        billingCycle: form.billingCycle,
        renewalNoticeDays: Number(form.renewalNoticeDays) || 0,
        autoRenew: form.autoRenew,
        poNo: form.poNo.trim(),
        notes: form.notes,
      });
      setNote({ ok: true, text: t('cn.done.added', { no: out.contractNo }) });
      setForm(EMPTY);
      setAdding(false);
      setReload((r) => r + 1);
    } catch (err) {
      const e = err instanceof ApiError ? err : null;
      setFields(e?.fields ?? {});
      setNote({ ok: false, text: t('cn.error.save') });
    } finally {
      setBusy(false);
    }
  }

  async function saveEdit(c: ContractView) {
    setBusy(true);
    setNote(null);
    try {
      await apiSend('PATCH', `/api/contracts/${encodeURIComponent(c.contractNo)}`, {
        etag: c.etag,
        ...(edit.end && edit.end !== c.end ? { end: edit.end } : {}),
        ...(edit.value !== '' && Number(edit.value) * 100 !== c.value
          ? { value: Number(edit.value) }
          : {}),
        autoRenew: edit.autoRenew,
        ...(edit.status !== c.status ? { status: edit.status } : {}),
      });
      setNote({ ok: true, text: t('cn.done.saved') });
      setEditing(null);
      setReload((r) => r + 1);
    } catch (err) {
      const e = err instanceof ApiError ? err : null;
      setNote({
        ok: false,
        text: e?.code === 'conflict' ? t('quote.error.conflict') : t('cn.error.save'),
      });
    } finally {
      setBusy(false);
    }
  }

  const input = (
    id: keyof Form,
    label: MessageKey,
    opts: { type?: string; dir?: 'ltr' | 'auto'; max?: number } = {},
  ) => (
    <div class="field">
      <label for={`cn-${id}`}>{t(label)}</label>
      <input
        id={`cn-${id}`}
        type={opts.type ?? 'text'}
        dir={opts.dir ?? 'auto'}
        maxLength={opts.max ?? 150}
        value={form[id] as string}
        aria-invalid={fields[id] ? true : undefined}
        onInput={(e) => set({ [id]: (e.target as HTMLInputElement).value })}
      />
      {fields[id] && <p class="field__error">{fields[id]}</p>}
    </div>
  );

  return (
    <div class="stack">
      <div class="actions cn-head">
        <button
          type="button"
          class="button button--primary"
          aria-expanded={adding}
          onClick={() => setAdding(!adding)}
        >
          {t('cn.add')}
        </button>
      </div>
      {note && (
        <p class={note.ok ? 'form-saved' : 'form-problem'} role={note.ok ? 'status' : 'alert'}>
          {note.text}
        </p>
      )}

      {adding && (
        <section class="card" aria-labelledby="cn-add">
          <h2 id="cn-add" class="card__title">
            {t('cn.addTitle')}
          </h2>
          <div class="cn-from-po">
            {input('poNo', 'cn.fromPo', { dir: 'ltr', max: 30 })}
            <button
              type="button"
              class="button"
              disabled={!form.poNo.trim()}
              onClick={() => void fromPo()}
            >
              {t('cn.fill')}
            </button>
          </div>
          <div class="reg-grid">
            {input('name', 'cn.name')}
            <div class="field">
              <label for="cn-supplier">{t('inv.col.supplier')}</label>
              <select
                id="cn-supplier"
                value={form.supplierId}
                aria-invalid={fields.supplierId ? true : undefined}
                onChange={(e) => set({ supplierId: (e.target as HTMLSelectElement).value })}
              >
                <option value="">{t('cn.pickSupplier')}</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
              {fields.supplierId && <p class="field__error">{fields.supplierId}</p>}
            </div>
            <div class="field">
              <label for="cn-entity">{t('po.entity')}</label>
              <select
                id="cn-entity"
                value={form.entity}
                onChange={(e) => set({ entity: (e.target as HTMLSelectElement).value })}
              >
                {['SMC', 'VIB', 'MED'].map((e) => (
                  <option key={e} value={e}>
                    {t(`cn.entity.${e}` as MessageKey)}
                  </option>
                ))}
              </select>
            </div>
            {input('department', 'cn.department')}
            {input('owner', 'cn.owner', { type: 'email', dir: 'ltr', max: 200 })}
            <div class="field">
              <label for="cn-billing">{t('cn.billing')}</label>
              <select
                id="cn-billing"
                value={form.billingCycle}
                onChange={(e) => set({ billingCycle: (e.target as HTMLSelectElement).value })}
              >
                {BILLING_CYCLES.map((b) => (
                  <option key={b} value={b}>
                    {t(`cn.billing.${b}` as MessageKey)}
                  </option>
                ))}
              </select>
            </div>
            {input('start', 'cn.start', { type: 'date', dir: 'ltr' })}
            {input('end', 'cn.end', { type: 'date', dir: 'ltr' })}
            {input('renewalNoticeDays', 'cn.noticeDays', { type: 'number', dir: 'ltr', max: 3 })}
            {input('value', 'cn.value', { type: 'number', dir: 'ltr', max: 15 })}
            <div class="field">
              <label for="cn-currency">{t('quote.currency')}</label>
              <select
                id="cn-currency"
                value={form.currency}
                onChange={(e) => set({ currency: (e.target as HTMLSelectElement).value })}
              >
                {CONTRACT_CURRENCIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div class="field field--check">
              <input
                id="cn-auto"
                type="checkbox"
                checked={form.autoRenew}
                onChange={(e) => set({ autoRenew: (e.target as HTMLInputElement).checked })}
              />
              <label for="cn-auto">{t('cn.autoRenew')}</label>
            </div>
          </div>
          {input('notes', 'cn.notes', { max: 1000 })}
          <div class="actions">
            <button
              type="button"
              class="button button--primary"
              disabled={busy}
              onClick={() => void save()}
            >
              {t('cn.save')}
            </button>
            <button type="button" class="button" onClick={() => setAdding(false)}>
              {t('cn.cancel')}
            </button>
          </div>
        </section>
      )}

      <div class="cn-tiles">
        <div class="cn-tile">
          <span class="caption">{t('cn.tile.active')}</span>
          <strong>{data.summary.active}</strong>
        </div>
        <div class={`cn-tile${data.summary.renewing90 ? ' cn-tile--warn' : ''}`}>
          <span class="caption">{t('cn.tile.renewing')}</span>
          <strong>{data.summary.renewing90}</strong>
        </div>
        <div class="cn-tile">
          <span class="caption">{t('cn.tile.auto')}</span>
          <strong>{data.summary.autoRenew}</strong>
        </div>
        <div class="cn-tile">
          <span class="caption">{t('cn.tile.committed')}</span>
          <strong dir="ltr">
            {Object.entries(data.summary.committed)
              .map(([cur, v]) => `${cur} ${compact(v)}`)
              .join(' · ') || '—'}
          </strong>
        </div>
      </div>

      <section class="card" aria-labelledby="cn-next">
        <h2 id="cn-next" class="card__title">
          {t('cn.next90')}
        </h2>
        <div class="cn-bands">
          {bands.map((b) => (
            <div key={b.key} class={`cn-band${b.notice ? ' cn-band--notice' : ''}`}>
              <h3 class="caption">{t(b.key as MessageKey)}</h3>
              {b.items.length === 0 ? (
                <p class="caption">—</p>
              ) : (
                <ul>
                  {b.items.map((c) => (
                    <li key={c.contractNo}>
                      <strong>{c.name}</strong>
                      <span class="caption"> · {t('cn.inDays', { n: String(c.daysToEnd) })}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </section>

      <section class="card" aria-labelledby="cn-list">
        <h2 id="cn-list" class="visually-hidden">
          {t('nav.contracts')}
        </h2>
        {data.contracts.length === 0 ? (
          <p>{t('cn.none')}</p>
        ) : (
          <div class="table-wrap">
            <table class="table cn-table">
              <thead>
                <tr>
                  <th scope="col">{t('cn.col.contract')}</th>
                  <th scope="col">{t('cn.col.entity')}</th>
                  <th scope="col">{t('cn.billing')}</th>
                  <th scope="col" class="num">
                    {t('cn.col.value')}
                  </th>
                  <th scope="col">{t('cn.col.ends')}</th>
                  <th scope="col">{t('cn.col.notice')}</th>
                  <th scope="col">{t('cn.col.auto')}</th>
                  <th scope="col">
                    <span class="visually-hidden">{t('admin.actions')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.contracts.map((c) => {
                  const live = c.status === 'active' || c.status === 'renewing';
                  return [
                    <tr key={c.contractNo} class={live ? '' : 'cn-row--ended'}>
                      <td>
                        <span class="req-row__title">{c.name}</span>
                        <span class="caption cell-sub">
                          <span dir="ltr">{c.contractNo}</span> · {c.supplierName} · {c.department}
                        </span>
                      </td>
                      <td>
                        <span class={`pill ${ENTITY_PILL[c.entity] ?? ''}`}>
                          {t(`cn.entity.${c.entity}` as MessageKey)}
                        </span>
                      </td>
                      <td>{t(`cn.billing.${c.billingCycle}` as MessageKey)}</td>
                      <td class="num" dir="ltr">
                        {c.currency} {whole(c.value)}
                      </td>
                      <td dir="ltr">{formatDate(c.end, lang)}</td>
                      <td>
                        {!live ? (
                          <span class="pill">{t(`cn.status.${c.status}` as MessageKey)}</span>
                        ) : c.daysToNotice < 0 ? (
                          <span class="pill pill--danger">{t('cn.noticePassed')}</span>
                        ) : c.daysToNotice <= 90 ? (
                          <span class="pill pill--warning">
                            {c.daysToNotice === 0
                              ? t('cn.today')
                              : t('cn.inDays', { n: String(c.daysToNotice) })}
                          </span>
                        ) : (
                          <span dir="ltr">{formatDate(c.noticeBy, lang)}</span>
                        )}
                      </td>
                      <td>{c.autoRenew ? t('cn.yes') : t('cn.no')}</td>
                      <td>
                        <div class="actions cn-actions">
                          {c.renewalPrNo ? (
                            <a class="button button--small" href={`#/requests/${c.renewalPrNo}`}>
                              <span dir="ltr">{c.renewalPrNo}</span>
                            </a>
                          ) : (
                            live && (
                              <a
                                class="button button--small"
                                href={`#/requests/new/renew/${c.contractNo}`}
                                aria-label={t('cn.renewFor', { name: c.name })}
                              >
                                {t('cn.renew')}
                              </a>
                            )
                          )}
                          <button
                            type="button"
                            class="button button--small"
                            aria-label={t('cn.editFor', { name: c.name })}
                            onClick={() => {
                              setEditing(editing === c.contractNo ? null : c.contractNo);
                              setEdit({
                                end: c.end,
                                value: String(c.value / 100),
                                autoRenew: c.autoRenew,
                                status: c.status,
                              });
                            }}
                          >
                            {t('cn.edit')}
                          </button>
                        </div>
                      </td>
                    </tr>,
                    editing === c.contractNo && (
                      <tr key={`${c.contractNo}-edit`} class="cn-edit">
                        <td colSpan={8}>
                          <div class="cn-edit__grid">
                            <div class="field">
                              <label for="cn-edit-end">{t('cn.end')}</label>
                              <input
                                id="cn-edit-end"
                                type="date"
                                dir="ltr"
                                value={edit.end}
                                aria-describedby="cn-edit-end-when"
                                onInput={(e) =>
                                  setEdit({ ...edit, end: (e.target as HTMLInputElement).value })
                                }
                              />
                              <DateHint id="cn-edit-end-when" value={edit.end} />
                            </div>
                            <div class="field">
                              <label for="cn-edit-value">{t('cn.value')}</label>
                              <input
                                id="cn-edit-value"
                                type="number"
                                dir="ltr"
                                value={edit.value}
                                onInput={(e) =>
                                  setEdit({ ...edit, value: (e.target as HTMLInputElement).value })
                                }
                              />
                            </div>
                            <div class="field">
                              <label for="cn-edit-status">{t('cn.status')}</label>
                              <select
                                id="cn-edit-status"
                                value={edit.status}
                                onChange={(e) =>
                                  setEdit({
                                    ...edit,
                                    status: (e.target as HTMLSelectElement).value,
                                  })
                                }
                              >
                                {CONTRACT_STATUSES.map((s) => (
                                  <option key={s} value={s}>
                                    {t(`cn.status.${s}` as MessageKey)}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div class="field field--check">
                              <input
                                id="cn-edit-auto"
                                type="checkbox"
                                checked={edit.autoRenew}
                                onChange={(e) =>
                                  setEdit({
                                    ...edit,
                                    autoRenew: (e.target as HTMLInputElement).checked,
                                  })
                                }
                              />
                              <label for="cn-edit-auto">{t('cn.autoRenew')}</label>
                            </div>
                          </div>
                          <p class="caption">{t('cn.editHint')}</p>
                          <div class="actions">
                            <button
                              type="button"
                              class="button button--primary button--small"
                              disabled={busy}
                              onClick={() => void saveEdit(c)}
                            >
                              {t('cn.save')}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ),
                  ];
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
